import { useCallback, useRef, useState, type ReactElement } from 'react';
import { MaterialIcon } from './MaterialIcon.js';

/**
 * Themed toast notifications (replaces window.alert across the app). A small bottom-center stack of
 * transient messages that inherit the active theme tokens, so prompts match the rest of the chrome
 * in both light and dark. Presentational `Toaster` + a tiny `useToasts` controller that owns the
 * list and the auto-dismiss timers.
 */
export type ToastTone = 'info' | 'success' | 'error';

/**
 * An optional call-to-action rendered inside the toast. Either a link (`href`, e.g. "Print label") or
 * an in-app callback (`onClick`, e.g. "Undo" for a LOOM badge delete). `onClick` wins when both are set.
 */
export interface ToastAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface Toast {
  id: number;
  /** Optional bold lead line. */
  title?: string;
  message: string;
  tone: ToastTone;
  action?: ToastAction;
}

const TONE_ICON: Record<ToastTone, string> = {
  info: 'info',
  success: 'check_circle',
  error: 'error',
};

export function Toaster({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }): ReactElement {
  return (
    <div className="lapo-toaster" data-testid="toaster" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`lapo-toast is-${t.tone}`} role="status">
          <span className="lapo-toast-icon">
            <MaterialIcon name={TONE_ICON[t.tone]} size={18} />
          </span>
          <div className="lapo-toast-body">
            {t.title && <div className="lapo-toast-title">{t.title}</div>}
            <div className="lapo-toast-msg">{t.message}</div>
            {t.action &&
              (t.action.onClick ? (
                <button
                  type="button"
                  className="lapo-toast-action"
                  onClick={() => {
                    t.action?.onClick?.();
                    onDismiss(t.id);
                  }}
                >
                  {t.action.label}
                </button>
              ) : (
                <a className="lapo-toast-action" href={t.action.href} target="_blank" rel="noreferrer">
                  {t.action.label}
                </a>
              ))}
          </div>
          <button type="button" className="lapo-toast-close" aria-label="Dismiss" onClick={() => onDismiss(t.id)}>
            <MaterialIcon name="close" size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

export interface ToastInput {
  title?: string;
  message: string;
  tone?: ToastTone;
  /** ms before auto-dismiss; 0 keeps it until closed. Defaults to 5000. */
  duration?: number;
  /** Optional action link (e.g. open a purchased shipping label). */
  action?: ToastAction;
}

export interface UseToasts {
  toasts: Toast[];
  push: (t: ToastInput) => number;
  dismiss: (id: number) => void;
}

export function useToasts(): UseToasts {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    ({ title, message, tone = 'info', duration = 5000, action }: ToastInput): number => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, title, message, tone, action }]);
      if (duration > 0) timers.current.set(id, setTimeout(() => dismiss(id), duration));
      return id;
    },
    [dismiss],
  );

  return { toasts, push, dismiss };
}
